import { FlipForwardIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

interface IAssistantSummaryBubbleProps {
  /**
   * The recap itself. Takes rendered content rather than a string so the text
   * flows through the transcript's own markdown pipeline and any inline entity
   * reference inside it still resolves to a chip.
   */
  children: React.ReactNode;
  className?: string;
}

/**
 * The assistant's recap of work it already did on an alert, rendered as a
 * bordered gray bubble with a curved arrow (Figma node `6153-318243`).
 *
 * Opening an alert always replays its stored summary. For an alert nobody has
 * worked yet that summary is news and reads as ordinary prose; for one already
 * read, waiting, or archived it is a recap of a past conversation, and this
 * treatment is what says so before the user reads a word of it.
 *
 * Only the recap belongs in here. The ask that closes a summary, and the
 * suggestion chips under it, stay outside as live prose — inside the bubble
 * they would read as part of the conversation that already happened.
 */
const AssistantSummaryBubble = ({ children, className }: IAssistantSummaryBubbleProps) => (
  <div
    className={cx(
      // Unfilled: the design outlines the recap rather than tinting it, so it
      // reads as set-apart without competing with the pane's own surface. The
      // line height is tighter than the transcript's `leading-relaxed` prose for
      // the same reason — a compact aside, not the reply the eye lands on.
      'border-utility-mist-300 text-secondary flex w-full items-start gap-2 rounded-2xl border px-4 py-3 text-sm leading-[18px]',
      className
    )}
  >
    <FlipForwardIcon aria-hidden="true" className="size-4 shrink-0" />
    <div className="min-w-0 flex-1">{children}</div>
  </div>
);

export default AssistantSummaryBubble;
